// Moteur d'ambiance — une seule source de vérité (passation design §7).
// Ajouter une ambiance ici la rend disponible partout : réglage super admin, vitrine,
// page de connexion, bande d'en-tête. Les visuels vivent dans public/amb/.

export type AmbianceKey = 'ciel' | 'maison' | 'printemps' | 'ete' | 'automne' | 'hiver' | 'noel' | 'halloween' | 'valentin';
export type AmbianceSetting = AmbianceKey | 'auto';
export type ThemeMode = 'L' | 'D';

/** Les quatre jetons d'accent : couleur, fond teinté, bordure teintée, texte sur fond teinté. */
export type AccentTokens = { acc: string; bg: string; bd: string; fg: string };

export interface Ambiance {
    label: string;
    description: string;
    L: [string, string, string, string];
    D: [string, string, string, string];
    bandL: string;
    bandD: string;
    /** Halo de soleil posé sur la bande (clair), sous les nuages. */
    sunL?: string;
    sunD?: string;
    /** Scène animée (clair et sombre), ou par thème avec sceneL / sceneD. */
    scene?: SceneKey;
    sceneL?: SceneKey;
    sceneD?: SceneKey;
    /** Vidéo de fond (ciel étoilé), limitée à un thème. */
    video?: string;
    videoOnly?: ThemeMode;
    /** Image fixe de repli quand la vidéo n'est pas voulue (mouvement réduit). */
    photo?: string;
    photoOnly?: ThemeMode;
    /** Voile posé sur la vidéo/photo pour garder le texte lisible. */
    veil?: string;
    /** Ambiance de fête : remplace l'accent de l'établissement (les autres le conservent). */
    festive: boolean;
}

export const AMB: Record<AmbianceKey, Ambiance> = {
    ciel: {
        label: 'Jour / Nuit',
        description: 'Le fond par défaut : nuages aquarelle en clair, ciel étoilé en sombre.',
        L: ['#3f7fa8', '#e9f2f8', '#c3dbe9', '#2c5a76'],
        D: ['#9aa4d6', '#171a2a', '#2f3550', '#c8cdec'],
        bandL: 'linear-gradient(180deg, #bfdcf0 0%, #d9ebf7 46%, #f3f8fc 100%)',
    sunL: 'radial-gradient(circle at 84% -30%, rgba(255,246,214,.95) 0%, rgba(255,238,190,.5) 26%, rgba(255,238,190,0) 62%)',
        bandD: '#05060a',
        sceneL: 'clouds',
        video: '/amb/ciel-etoile-4k.mp4', videoOnly: 'D',
        photo: '/amb/ciel-etoile-4k.jpg', photoOnly: 'D',
        veil: 'linear-gradient(90deg, rgba(5,6,10,.78) 0%, rgba(5,6,10,.34) 54%, rgba(5,6,10,.04) 100%)',
        festive: false,
    },
    maison: {
        label: 'Maison',
        description: 'Votre couleur, aucune animation. Pour qui ne veut pas de mouvement.',
        L: ['#987b55', '#f4ede1', '#dcc9a8', '#6d5738'],
        D: ['#c9ab7d', '#2a2318', '#4a3d28', '#d9c7a8'],
        bandL: '#f0f1f3', bandD: '#0e1116',
        festive: false,
    },
    printemps: {
        label: 'Printemps',
        description: 'Fleurs et papillon en descente lente.',
        L: ['#b3567c', '#fbeef3', '#f0cfdd', '#8a3f5e'],
        D: ['#d98aa8', '#2a1a21', '#4d2b37', '#f0c2d2'],
        bandL: 'linear-gradient(180deg, #fbeaf1, #fdf6f9)',
        bandD: 'linear-gradient(180deg, #1c1218, #0d0a0c)',
        scene: 'bloom', festive: true,
    },
    ete: {
        label: 'Été',
        description: 'Soleil, palmiers, flamant, étoile de mer.',
        L: ['#a8791f', '#fbf1dc', '#ecd7a8', '#7a5714'],
        D: ['#d9ac4e', '#2a2313', '#4d3f1f', '#f0d99e'],
        bandL: 'linear-gradient(180deg, #fbf2dc, #fdf9ee)',
        bandD: 'linear-gradient(180deg, #1c1810, #0d0b08)',
        scene: 'beach', festive: true,
    },
    automne: {
        label: 'Automne',
        description: 'Feuilles peintes qui pivotent en tombant.',
        L: ['#a4551f', '#fbeee4', '#ecd0b4', '#7a3f16'],
        D: ['#d98a4e', '#2a1d13', '#4d3320', '#f0c69e'],
        bandL: 'linear-gradient(180deg, #fbe9dc, #fdf5ef)',
        bandD: 'linear-gradient(180deg, #1c140e, #0d0a07)',
        scene: 'leaves', festive: true,
    },
    hiver: {
        label: 'Hiver',
        description: 'Flocons dessinés, chute lente avec dérive latérale.',
        L: ['#3f6f96', '#eaf2f8', '#c2d8e8', '#2d5273'],
        D: ['#6fa3c9', '#131f28', '#28414f', '#b9d7ea'],
        bandL: 'linear-gradient(180deg, #e6eff6, #f5f9fc)',
        bandD: 'linear-gradient(180deg, #101820, #0a0c10)',
        scene: 'snow', festive: true,
    },
    noel: {
        label: 'Noël',
        description: 'Père Noël, sapin et bonhomme de neige.',
        L: ['#9c3634', '#fbecec', '#ecc6c5', '#722625'],
        D: ['#d97a78', '#2a1616', '#4d2a29', '#f0b8b7'],
        bandL: 'linear-gradient(180deg, #f6e3e3, #fdf5f5)',
        bandD: 'linear-gradient(180deg, #1c1010, #0d0808)',
        scene: 'santas', festive: true,
    },
    halloween: {
        label: 'Halloween',
        description: 'Citrouille et chauve-souris en descente lente.',
        L: ['#a3551a', '#fbeee1', '#ecd0af', '#7a3f13'],
        D: ['#d9873a', '#2a1d10', '#4d331c', '#f0c396'],
        bandL: 'linear-gradient(180deg, #f9ecdd, #fdf7f0)',
        bandD: 'linear-gradient(180deg, #1a1108, #0b0805)',
        scene: 'spooky', festive: true,
    },
    valentin: {
        label: 'St-Valentin',
        description: 'Cupidon et bouquet de roses.',
        L: ['#a83f5c', '#fbecf0', '#ecc6d1', '#7a2c42'],
        D: ['#d97a95', '#2a161b', '#4d2a32', '#f0b8c6'],
        bandL: 'linear-gradient(180deg, #f9e8ed, #fdf5f7)',
        bandD: 'linear-gradient(180deg, #1c0f13, #0d0708)',
        scene: 'hearts', festive: true,
    },
};

export const ORDER: AmbianceSetting[] = ['auto', 'ciel', 'maison', 'printemps', 'ete', 'automne', 'hiver', 'noel', 'halloween', 'valentin'];

export const AUTO_DESCRIPTION = 'Suit la date : Noël en décembre, Halloween à partir du 20 octobre, St-Valentin jusqu\'au 20 février, sinon Jour / Nuit.';

export type SceneKey = 'clouds' | 'bloom' | 'beach' | 'leaves' | 'snow' | 'santas' | 'spooky' | 'hearts';

export interface Scene {
    list: string[];
    /** Nuages : dérive horizontale au lieu d'une chute. */
    drift?: boolean;
    /** Taille de base (px) et pas entre les tailles. */
    min?: number;
    step?: number;
    /** Durée de base ajoutée à celle de la graine. */
    dur?: number;
    /** Nombre de visuels à l'écran. */
    n: number;
}

export const SPRITES: Record<SceneKey, Scene> = {
    clouds: { list: ['/amb/jour/1.png', '/amb/jour/2.png', '/amb/jour/3.png'], drift: true, n: 9 },
    bloom: { list: ['/amb/printemps/printemps-1.png', '/amb/printemps/printemps-2.png', '/amb/printemps/printemps-3.png', '/amb/printemps/printemps-4.png'], min: 18, step: 8, dur: 13, n: 12 },
    beach: { list: ['/amb/ete/ete-1.png', '/amb/ete/ete-2.png', '/amb/ete/ete-3.png', '/amb/ete/ete-4.png', '/amb/ete/ete-5.png', '/amb/ete/ete-6.png'], min: 20, step: 9, dur: 12, n: 11 },
    // Feuilles (1-3), puis champignons, glands et bouquet de feuilles ajoutés le 19/09.
    leaves: { list: ['/amb/automne/1.png', '/amb/automne/2.png', '/amb/automne/3.png', '/amb/automne/4.png', '/amb/automne/5.png', '/amb/automne/6.png'], min: 15, step: 7, dur: 10, n: 14 },
    // Flocon, puis chocolat chaud et patins (19/09).
    snow: { list: ['/amb/hiver/hiver-1.png', '/amb/hiver/hiver-2.png', '/amb/hiver/hiver-3.png'], min: 11, step: 6, dur: 9, n: 16 },
    santas: { list: ['/amb/noel/noel-1.png', '/amb/noel/noel-2.png', '/amb/noel/noel-3.png'], min: 20, step: 8, dur: 13, n: 11 },
    // Chapeau, toile, fantôme et araignée ajoutés le 19/09.
    spooky: { list: ['/amb/halloween/halloween-1.png', '/amb/halloween/halloween-2.png', '/amb/halloween/halloween-3.png', '/amb/halloween/halloween-4.png', '/amb/halloween/halloween-5.png', '/amb/halloween/halloween-6.png'], min: 21, step: 9, dur: 11, n: 11 },
    hearts: { list: ['/amb/valentin/valentin-1.png', '/amb/valentin/valentin-2.png', '/amb/valentin/valentin-3.png'], min: 20, step: 9, dur: 13, n: 11 },
};

// Silhouettes noires : illisibles sur fond sombre, on les passe en blanc.
export const DARK_INVERT: Record<string, true> = { '/amb/halloween/halloween-1.png': true, '/amb/halloween/halloween-6.png': true };

export const isAmbianceSetting = (v: unknown): v is AmbianceSetting =>
    typeof v === 'string' && (ORDER as string[]).includes(v);

/**
 * Période du calendrier des ambiances (réglée par le super admin) : « Noël du 20 au 26 décembre ».
 * Dates au format YYYY-MM-DD ; `yearly` = se répète chaque année (l'année saisie sert alors de repère).
 * Une période peut chevaucher le 1er janvier (du 20 décembre au 5 janvier).
 */
export interface AmbiancePeriod {
    key: AmbianceKey;
    from: string;
    to: string;
    yearly?: boolean;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isAmbiancePeriod = (v: unknown): v is AmbiancePeriod => {
    const p = v as AmbiancePeriod;
    return !!p && typeof p === 'object' && typeof p.key === 'string' && p.key in AMB
        && typeof p.from === 'string' && DATE_RE.test(p.from) && typeof p.to === 'string' && DATE_RE.test(p.to);
};

const dayOfYear = (mmdd: string): number => { const [m, d] = mmdd.split('-').map(Number); return m * 100 + d; };

/** La période couvre-t-elle cette date ? Comparaison par jour, sans heure. */
export function periodCovers(p: AmbiancePeriod, now: Date): boolean {
    const y = now.getFullYear();
    const today = `${y}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (p.yearly === false) return p.from <= today && today <= p.to;
    const a = dayOfYear(p.from.slice(5)), b = dayOfYear(p.to.slice(5)), t = dayOfYear(today.slice(5));
    return a <= b ? (a <= t && t <= b) : (t >= a || t <= b); // à cheval sur le nouvel an
}

/** Le calendrier prime ; la première période qui couvre aujourd'hui gagne. */
export function resolveScheduled(schedule: AmbiancePeriod[] | null | undefined, now: Date = new Date()): AmbianceKey | null {
    if (!Array.isArray(schedule)) return null;
    const hit = schedule.find((p) => isAmbiancePeriod(p) && periodCovers(p, now));
    return hit ? hit.key : null;
}

/** « Auto » : les fêtes priment selon la date, sinon le ciel par défaut. Le calendrier passe avant tout. */
export function resolveAmbiance(wanted: AmbianceSetting | null | undefined, now: Date = new Date(), schedule?: AmbiancePeriod[] | null): AmbianceKey {
    const scheduled = resolveScheduled(schedule, now);
    if (scheduled) return scheduled;
    const w = wanted || 'ciel';
    if (w !== 'auto') return AMB[w as AmbianceKey] ? (w as AmbianceKey) : 'ciel';
    const m = now.getMonth() + 1;
    const day = now.getDate();
    if (m === 12) return 'noel';
    if (m === 10 && day >= 20) return 'halloween';
    if (m === 2 && day <= 20) return 'valentin';
    return 'ciel';
}

/** Les quatre jetons d'accent d'une ambiance, pour un thème donné. */
export function accents(key: AmbianceKey, mode: ThemeMode): AccentTokens {
    const t = AMB[key] || AMB.ciel;
    const v = mode === 'D' ? t.D : t.L;
    return { acc: v[0], bg: v[1], bd: v[2], fg: v[3] };
}

export interface SpriteSeed { left: number; dur: number; delay: number; dx: number; rot: number }

/** Graines déterministes : même disposition à chaque rendu, pas de saut au re-render. */
export function seeds(n: number): SpriteSeed[] {
    return Array.from({ length: n }, (_, i) => ({
        left: ((i * 13.37) % 94) + 3,
        dur: 8 + (i % 7) * 1.5,
        delay: (i % 9) * 0.9,
        dx: ((i % 7) - 3) * 18,
        rot: ((i % 6) - 3) * 120,
    }));
}
